// Libs
import when from 'when'
import Emitter from 'event-emitter'
import glmat from 'gl-matrix'

// Strcutral
import SceneAbs from '../abstract/scene'
import Camera from '../utils/camera'
import TextureLoader from '../utils/textureLoaderObject'
import Manifest from '../manifest'

// Entities
import Plane from '../entities/plane'
import Background from '../entities/background'
import ImageTransition from '../entities/imageTransition'

// Post
import PostMarbling from '../postProcess/marblingDisplacement'

// Utils
import RAF from '@/js/utils/raf'
import DebugController from '@/glxp/debug/debugController'
import { TweenMax } from 'gsap'

const MARGIN_BOTTOM = 100
const SHAKE_DAMPING = 0.3
const SHAKE_CURSOR_DAMPING = 0.1
const SHAKE_STEP_DAMPING = 0.025
const SHAKE_THRESHOLD = 0.05

let mat4 = glmat.mat4
let vec3 = glmat.vec3
let vec2 = glmat.vec2

const _VEC2 = vec2.create()

var _emitter = {}
Emitter(_emitter)

class TransitionScene extends SceneAbs {
  constructor (container, manager = null) {
    super(container, manager)

    this.camera = new Camera(this, 30)
    this.camera.node.position[2] = -6
    this.shakeFactor = 0
    this.shakeFactorTarget = 0
    this.step = 0
    this.stepTarget = 0
    this.cursor = vec2.create()
    this.cursorTarget = vec2.create()
    this.cursorWindow = vec2.create()
    this.lastCursor = vec2.create()
    this.velocity = vec2.create()
    this.marginBottom = MARGIN_BOTTOM * this.dpr

    this.clearColor = [0, 0, 0, 0]
    this.color = [0, 0, 0]

    this.activeTexture = null
    this.transitionTexture = null
    this.transitionImagePlane = null

    this.mobile = false

    this.textureLoader = new TextureLoader(this)
    this.plane = new Plane(this, 'shake', 'shake', 'transition-shake')

    this.background = new Background(this, false)
    this.background2 = new Background(this, false)
    this.useColor = true
    this.showLogo = false
    this.offset = 1
    this.shift = 0
    this.lowPerf = false
    this.perfDelayedCalls = 0

    this.postMarbling = new PostMarbling(this)

    this.config = {
      shake_damping: { value: 0.3, range: [0, 0.1] },
      cursor_damping: { value: 0.1, range: [0, 0.1] },
      shake_position: { value: 1, range: [-10, 10] },
      desktop_shake_scale: { value: 0.9, range: [0, 2] },
      mobile_shake_scale: { value: 0.4, range: [0, 2] },
      perf_threshold: { value: 150, range: [0, 1000] }
    }

    DebugController.addConfig(this.config, 'Transition')

    // this.initEvents()
  }

  onLoaded () {
    this.active = true
    this._emitter.emit('loaded')
  }

  start () {
    // this.shakeFactor = .05
  }

  setBackgroundColor (hex) {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (result) {
      this.color[0] = parseInt(result[1], 16) / 255,
      this.color[1] = parseInt(result[2], 16) / 255,
      this.color[2] = parseInt(result[3], 16) / 255
    }
  }

  initEvents () {
    this.container.addEventListener('mousemove', (event) => {
      this.cursorTarget[0] = event.clientX / ((this.width / this.dpr))
      this.cursorTarget[1] = event.clientY / ((this.height / this.dpr))
    })

    window.addEventListener('mousemove', (event) => {
      this.cursorWindow[0] = event.clientX / ((this.width / this.dpr))
      this.cursorWindow[1] = event.clientY / ((this.height / this.dpr))
    })
  }

  load (id, url) {
    let loadables = []

    for (const key in Manifest.transition) {
      if (Manifest.transition.hasOwnProperty(key)) {
        const element = Manifest.transition[key].url
        loadables.push(this.textureLoader.load(element, key, Manifest.transition[key].options))
      }
    }

    when.all(loadables).then(() => {
      this.onLoaded()
    })

    let percent = 0
    for (let i = 0; i < loadables.length; i++) {
      loadables[i].then(() => {
        percent++
        this._emitter.emit('progress', Math.round((percent / loadables.length) * 100))
      })
    }
  }

  startImageTransition (image, bounds) {
    this.active = false
    this.inTransition = true
    this.transitionImagePlane = null

    this.shift = 0

    let rect = [
      bounds.left / (this.width / this.dpr),
      bounds.top / (this.height / this.dpr),
      bounds.width / (this.width / this.dpr),
      bounds.height / (this.height / this.dpr)
    ]

    // this.cursorTarget[0] = this.cursorWindow[0]
    // this.cursorTarget[1] = this.cursorWindow[1]
    this.textureLoader.load(image, 'image-transition', []).then(() => {
      this.launchImageTransitionAnimation(rect, bounds)
    })
  }

  launchImageTransitionAnimation (rect, bounds) {
    this.active = true
    this.transitionImagePlane = new ImageTransition(this, rect)
    this.transitionImagePlane.alpha = 1
    // TweenMax.set(this.transitionImagePlane, { alpha: 1, delay: 1  })
    this.postMarbling.pass.customShiftVertical = true
    this.postMarbling.pass.customShiftWidth = 0.5

    let timeline = new TimelineMax()
    if (this.mobile) document.querySelector('.transition').classList.add('in-transition')
    this.background.opacity = 0
    timeline.fromTo(this.background, this.mobile ? 1 : 0.5 , {
      opacity: 0
    }, {
      opacity: 1,
      onComplete: () => {
        document.body.classList.add('in-transition')
        setTimeout(() => {
          if (this.mobile) document.querySelector('.transition').classList.remove('in-transition')
        }, 1000)
        TweenMax.to(this.background,  0.5 , { opacity: 0,
          delay: 1,
          onComplete: () => {
              this.background.opacity = 1
          } })
      }
    })
      .fromTo(this.transitionImagePlane, 1.3, {
        progress: 0,
        imgSizeX: bounds.width,
        imgSizeY: bounds.height
      }, {
        progress: 1,
        imgSizeX: this.width / this.dpr,
        imgSizeY: document.querySelector('.transition-cover-helper').offsetHeight,
        ease: Power4.easeInOut,
        onComplete: () => {
          if (this.mobile) {
            setTimeout(() => {
              this.removeImageTransition()
            }, 500)
            window.scrollTo(0, 0)
          }
        }
      }, this.mobile ? '-=0.75' : '-=0.3')

    if (!this.mobile) {
      timeline.fromTo(this, 0.3, {
        shift: 0
      }, {
        shift: 0.075,
        ease: Power2.easeIn
      }, '-=1.3')
        .to(this, 1, {
          shift: 0,
          ease: Power2.easeOut,
          onComplete: () => {
            setTimeout(() => {
              this.removeImageTransition()
            }, 1000)
          }
        }, '-=1')
    }
  }

  removeImageTransition () {
    this.inTransition = false
    this.transitionImagePlane.kill()
    this.transitionImagePlane = null
    this.postMarbling.pass.customShiftVertical = false
    this.postMarbling.pass.customShiftWidth = 0.8
    this.background.opacity = 1

    setTimeout(() => {
      document.body.classList.remove('in-transition')
    }, 200)
  }

  applyDefaultState () {
    let gl = this.gl
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    gl.enable(gl.BLEND)
    gl.disable(gl.DEPTH_TEST)
    // gl.disable(gl.CULL_FACE)
  }

  customBlending () {
    let gl = this.gl
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_COLOR)
    gl.enable(gl.BLEND)
    gl.disable(gl.DEPTH_TEST)
  }

  // Pre-render
  preRender () {
    let gl = this.gl

    if (RAF.dt > this.config.perf_threshold.value) {
      this.perfDelayedCalls++
    }
    if (this.perfDelayedCalls > 10) {
      if (this.lowPerf == false) {
        console.log('switching to low perf mode')
      }
      this.lowPerf = true
    }

    this.plane.node.position[2] = -1
    this.plane.node.needUpdate = true

    this.postMarbling.pass.customShift = this.shift
    this.postMarbling.pass.customShiftWidth = 0.8

    if (this.mobile) {
      this.plane.customSettings.shakeScale = this.config.mobile_shake_scale.value
    } else {
      this.plane.customSettings.shakeScale = this.config.desktop_shake_scale.value
    }
  }

  // Render
  render () {
    if (!this.active) { return }
    this.time += RAF.dt / 1000
    this.dt = RAF.dt

    let gl = this.gl

    gl.clearColor(this.clearColor[0], this.clearColor[1], this.clearColor[2], this.clearColor[3])
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    gl.colorMask(true, true, true, true)

    this.preRender()
    gl.viewport(0, 0, this.width, this.height)

    // Render Time

    if (!this.mobile && !this.lowPerf) {
      this.postMarbling.preRender()
    }

    // pannel

    gl.viewport(this.width * this.offset, 0, this.width + this.width * this.offset, this.height)
    let tmp = [0, 0, 0]
    tmp[0] = this.color[0]; tmp[1] = this.color[1]; tmp[2] = this.color[2]
    this.color[0] = 1; this.color[1] = 1; this.color[2] = 1
    this.background2.render()

    gl.viewport(this.width * this.offset * 1.08, 0, this.width + this.width * this.offset * 1.08, this.height)
    this.color[0] = tmp[0]; this.color[1] = tmp[1]; this.color[2] = tmp[2]
    this.background.render()

    if (this.showLogo) {
      this.plane.render()
    }

    // image

    gl.viewport(0, 0, this.width, this.height)
    if (this.transitionImagePlane) {
      tmp[0] = this.color[0]; tmp[1] = this.color[1]; tmp[2] = this.color[2]
      this.color[0] = 1; this.color[1] = 1; this.color[2] = 1
      this.background.render()
      this.color[0] = tmp[0]; this.color[1] = tmp[1]; this.color[2] = tmp[2]
      this.transitionImagePlane.render()
    }

    if (!this.mobile && !this.lowPerf) {
      this.postMarbling.postRender()
      this.postMarbling.render()
    }

    this.postRender()
  }

  getCanvas () {

  }

  postRender () {
    this.gl.viewport(0, 0, this.width, this.height)
    // this.grain.render()
  }
}
export default TransitionScene
